// MissingPersonRegistrationPage.js - 텍스트 에어리어를 미리보기로 만드는 방식

import React, { useState, useEffect, useRef } from 'react';
import './MissingPersonRegistrationPage.css';

const MissingPersonRegistrationPage = () => {
  const [messageType, setMessageType] = useState('normal');
  const [formData, setFormData] = useState({
    image: null,
    imageSize: 0,
    imageName: '',
    expiryDate: '',
    expiryTime: '',
    messageContent: '',
    textAlign: 'center',
    fontSize: '16',
    textColor: '#FFFF00',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none'
  });

  const [disasterFormData, setDisasterFormData] = useState({
    disasterType: '',
    emergencyLevel: '안전안내',
    region: '울산광역시',
    agency: '울산광역시',
    image: null,
    imageSize: 0,
    imageName: '',
    expiryDate: '',
    expiryTime: '',
    messageContent: '',
    textAlign: 'center',
    fontSize: '16',
    textColor: '#FFFF00',
    fontWeight: 'normal',
    fontStyle: 'normal',
    textDecoration: 'none'
  });

  const [imagePreview, setImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // 텍스트 입력 참조
  const textareaRef = useRef(null);

  // 서버 연결 테스트 함수
  const testServerConnection = async () => {
    try {
      const response = await fetch('/api/status');
      const result = await response.json();
      console.log('서버 연결 테스트:', result);
      return true;
    } catch (error) {
      console.error('서버 연결 실패:', error);
      return false;
    }
  };

  // 기본 만료시점 설정
  const getDefaultExpiryDateTime = () => {
    const now = new Date();
    const defaultExpiry = new Date(now.getTime() + (7 * 24 * 60 * 60 * 1000)); // 7일 후

    const date = defaultExpiry.toISOString().split('T')[0];
    const time = defaultExpiry.toTimeString().slice(0, 5);

    return { date, time };
  };

  useEffect(() => {
    const { date, time } = getDefaultExpiryDateTime();

    setFormData(prev => ({
      ...prev,
      expiryDate: date,
      expiryTime: time
    }));

    setDisasterFormData(prev => ({
      ...prev,
      expiryDate: date,
      expiryTime: time
    }));
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (messageType === 'normal') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    } else {
      setDisasterFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // 텍스트 스타일 변경 함수들
  const handleStyleChange = (styleType, value) => {
    if (messageType === 'normal') {
      setFormData(prev => ({
        ...prev,
        [styleType]: value
      }));
    } else {
      setDisasterFormData(prev => ({
        ...prev,
        [styleType]: value
      }));
    }
  };

  const handleTextChange = (e) => {
    const { value } = e.target;
    if (messageType === 'normal') {
      setFormData(prev => ({
        ...prev,
        messageContent: value
      }));
    } else {
      setDisasterFormData(prev => ({
        ...prev,
        messageContent: value
      }));
    }
  };

  const handleMessageTypeChange = (type) => {
    setMessageType(type);
    setImagePreview(null);
    setSubmitMessage('');

    const { date, time } = getDefaultExpiryDateTime();

    if (type === 'normal') {
      setFormData({
        image: null,
        imageSize: 0,
        imageName: '',
        expiryDate: date,
        expiryTime: time,
        messageContent: '',
        textAlign: 'center',
        fontSize: '16',
        textColor: '#FFFF00',
        fontWeight: 'normal',
        fontStyle: 'normal',
        textDecoration: 'none'
      });
    } else {
      setDisasterFormData({
        disasterType: '',
        emergencyLevel: '안전안내',
        region: '울산광역시',
        agency: '울산광역시',
        image: null,
        imageSize: 0,
        imageName: '',
        expiryDate: date,
        expiryTime: time,
        messageContent: '',
        textAlign: 'center',
        fontSize: '16',
        textColor: '#FFFF00',
        fontWeight: 'normal',
        fontStyle: 'normal',
        textDecoration: 'none'
      });
    }

    // 텍스트 입력 내용 초기화
    if (textareaRef.current) {
      textareaRef.current.value = '';
    }
  };

  // 이미지 처리
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setSubmitMessage('이미지 파일만 업로드 가능합니다.');
        return;
      }

      if (file.size > 2 * 1024 * 1024) {
        setSubmitMessage('이미지 파일 크기는 2MB를 초과할 수 없습니다.');
        return;
      }

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = 128;
        canvas.height = 128;

        ctx.fillStyle = '#000000';
        ctx.fillRect(0, 0, 128, 128);
        ctx.drawImage(img, 0, 0, 128, 128);

        const pngBase64 = canvas.toDataURL('image/png');

        const reader = new FileReader();
        reader.onload = (e) => {
          const originalBase64 = e.target.result;

          if (messageType === 'normal') {
            setFormData(prev => ({
              ...prev,
              image: originalBase64,
              imageSize: file.size,
              imageName: file.name
            }));
          } else {
            setDisasterFormData(prev => ({
              ...prev,
              image: originalBase64,
              imageSize: file.size,
              imageName: file.name
            }));
          }

          setImagePreview(pngBase64);
        };
        reader.readAsDataURL(file);
      };

      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        img.src = e.target.result;
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const serverConnected = await testServerConnection();
      if (!serverConnected) {
        throw new Error('서버에 연결할 수 없습니다. 서버가 실행 중인지 확인해주세요.');
      }

      let validationError = null;
      const currentData = messageType === 'normal' ? formData : disasterFormData;

      // 기본 검증
      if (!currentData.messageContent || currentData.messageContent.trim() === '') {
        validationError = '메시지 내용을 입력해주세요.';
      }

      // 재난문자 추가 검증
      if (messageType === 'disaster') {
        if (!disasterFormData.disasterType || !disasterFormData.region) {
          validationError = '재난 유형과 지역을 모두 입력해주세요.';
        }
      }

      // 만료시점 검증
      if (!currentData.expiryDate || !currentData.expiryTime) {
        validationError = '만료 일시를 설정해주세요.';
      } else {
        const expiryDateTime = new Date(`${currentData.expiryDate}T${currentData.expiryTime}`);
        const now = new Date();
        if (expiryDateTime <= now) {
          validationError = '만료 일시는 현재 시간보다 이후여야 합니다.';
        }
      }

      if (validationError) {
        throw new Error(validationError);
      }

      // 요청 데이터 준비
      let requestData = {
        messageType: messageType,
        messageContent: currentData.messageContent,
        textAlign: currentData.textAlign,
        fontSize: currentData.fontSize,
        textColor: currentData.textColor,
        fontWeight: currentData.fontWeight,
        fontStyle: currentData.fontStyle,
        textDecoration: currentData.textDecoration,
        expiryDateTime: new Date(`${currentData.expiryDate}T${currentData.expiryTime}`).toISOString(),
        image: currentData.image,
        imageSize: currentData.imageSize,
        imageName: currentData.imageName
      };

      // 재난문자 추가 정보
      if (messageType === 'disaster') {
        requestData = {
          ...requestData,
          disasterType: disasterFormData.disasterType,
          emergencyLevel: disasterFormData.emergencyLevel,
          region: disasterFormData.region,
          agency: disasterFormData.agency
        };
      }

      console.log('전송할 데이터:', {
        ...requestData,
        image: requestData.image ? `[Base64 데이터 ${requestData.imageSize} bytes]` : '없음'
      });

      const response = await fetch('/api/messages/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestData)
      });

      console.log('서버 응답 상태:', response.status);

      const result = await response.json();
      console.log('서버 응답 데이터:', result);

      if (response.ok) {
        let successMessage = `메시지가 성공적으로 등록되었습니다. ${result.message || ''}`;

        if (result.generatedFiles) {
          successMessage += `\n\n생성된 파일:`;
          if (result.generatedFiles.textImage) {
            successMessage += `\n• 텍스트 정보: ${result.generatedFiles.textImage.fileName} (${Math.round(result.generatedFiles.textImage.size / 1024)}KB)`;
          }
          if (result.generatedFiles.userImage) {
            successMessage += `\n• 사용자 사진: ${result.generatedFiles.userImage.fileName} (${Math.round(result.generatedFiles.userImage.size / 1024)}KB)`;
          }
        }

        if (result.topicInfo) {
          successMessage += `\n\n토픽 전송 정보:`;
          successMessage += `\n• 토픽: ${result.topicInfo.topic}`;
          successMessage += `\n• 상태: ${result.topicInfo.status}`;
        }

        setSubmitMessage(successMessage);

        // 폼 초기화
        const { date, time } = getDefaultExpiryDateTime();

        if (messageType === 'normal') {
          setFormData({
            image: null,
            imageSize: 0,
            imageName: '',
            expiryDate: date,
            expiryTime: time,
            messageContent: '',
            textAlign: 'center',
            fontSize: '16',
            textColor: '#FFFF00',
            fontWeight: 'normal',
            fontStyle: 'normal',
            textDecoration: 'none'
          });
        } else {
          setDisasterFormData({
            disasterType: '',
            emergencyLevel: '안전안내',
            region: '울산광역시',
            agency: '울산광역시',
            image: null,
            imageSize: 0,
            imageName: '',
            expiryDate: date,
            expiryTime: time,
            messageContent: '',
            textAlign: 'center',
            fontSize: '16',
            textColor: '#FFFF00',
            fontWeight: 'normal',
            fontStyle: 'normal',
            textDecoration: 'none'
          });
        }

        setImagePreview(null);
        if (textareaRef.current) {
          textareaRef.current.value = '';
        }
      } else {
        console.error('서버 응답 오류:', result);
        throw new Error(result.message || `서버 오류 (${response.status}): 등록에 실패했습니다.`);
      }
    } catch (error) {
      console.error('등록 오류:', error);
      setSubmitMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentData = messageType === 'normal' ? formData : disasterFormData;

  return (
    <div className="missing-person-registration-page">
      <div className="page-header">
        <h1>메시지 등록</h1>
        <p className="subtitle">텍스트 입력 후 스타일을 설정하여 PNG 이미지로 생성됩니다</p>
      </div>

      <div className="registration-container">
        <form onSubmit={handleSubmit} className="registration-form">
          {/* 메시지 타입 토글 */}
          <div className="message-type-toggle">
            <h3>메시지 유형 선택</h3>
            <div className="toggle-buttons">
              <button
                type="button"
                className={`toggle-btn ${messageType === 'normal' ? 'active' : ''}`}
                onClick={() => handleMessageTypeChange('normal')}
              >
                일반 메시지
              </button>
              <button
                type="button"
                className={`toggle-btn ${messageType === 'disaster' ? 'active' : ''}`}
                onClick={() => handleMessageTypeChange('disaster')}
              >
                재난문자
              </button>
            </div>
          </div>

          {/* 상단 이미지 업로드 영역 */}
          <div className="image-upload-section">
            <h3>관련 사진 (128x128으로 변환됩니다)</h3>
            <div className="image-upload-container">
              <div className="image-preview-area">
                {imagePreview ? (
                  <div className="image-preview-wrapper">
                    <img
                      src={imagePreview}
                      alt="관련 사진 미리보기 (128x128)"
                      className="image-preview"
                      style={{ width: '128px', height: '128px', objectFit: 'cover' }}
                    />
                    <div className="image-info">
                      <small>128x128 PNG 미리보기</small>
                    </div>
                  </div>
                ) : (
                  <div className="image-placeholder">
                    <div className="placeholder-icon">📷</div>
                    <p>사진 업로드</p>
                    <small>128x128 PNG로 변환</small>
                  </div>
                )}
              </div>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageChange}
                className="image-input"
              />
              <label htmlFor="image-upload" className="image-upload-btn">
                사진 선택 (PNG 변환)
              </label>
            </div>
          </div>

          {/* 재난문자 추가 정보 */}
          {messageType === 'disaster' && (
            <div className="disaster-info-section">
              <h3>재난문자 정보</h3>
              <div className="disaster-form-container">
                <div className="form-group">
                  <label htmlFor="disasterType">재난 유형 *</label>
                  <select
                    id="disasterType"
                    name="disasterType"
                    value={disasterFormData.disasterType}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">재난 유형 선택</option>
                    <option value="호우">호우</option>
                    <option value="태풍">태풍</option>
                    <option value="대설">대설</option>
                    <option value="한파">한파</option>
                    <option value="폭염">폭염</option>
                    <option value="강풍">강풍</option>
                    <option value="지진">지진</option>
                    <option value="화재">화재</option>
                    <option value="산불">산불</option>
                    <option value="교통통제">교통통제</option>
                    <option value="정전">정전</option>
                    <option value="기타">기타</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="emergencyLevel">긴급도 *</label>
                  <select
                    id="emergencyLevel"
                    name="emergencyLevel"
                    value={disasterFormData.emergencyLevel}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="안전안내">안전안내</option>
                    <option value="관심">관심</option>
                    <option value="주의">주의</option>
                    <option value="경계">경계</option>
                    <option value="심각">심각</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="region">수신 지역 *</label>
                  <input
                    type="text"
                    id="region"
                    name="region"
                    value={disasterFormData.region}
                    onChange={handleInputChange}
                    placeholder="수신 지역을 입력하세요 (예: 울산광역시 남구)"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="agency">발신 기관</label>
                  <input
                    type="text"
                    id="agency"
                    name="agency"
                    value={disasterFormData.agency}
                    onChange={handleInputChange}
                    placeholder="발신 기관을 입력하세요 (예: 울산광역시)"
                  />
                </div>
              </div>
            </div>
          )}

          {/* 만료시점 */}
          <div className="form-group expiry-group">
            <label htmlFor="expiry">만료 일시 *</label>
            <div className="expiry-inputs">
              <input
                type="date"
                id="expiryDate"
                name="expiryDate"
                value={currentData.expiryDate}
                onChange={handleInputChange}
                min={new Date().toISOString().split('T')[0]}
                required
              />
              <input
                type="time"
                id="expiryTime"
                name="expiryTime"
                value={currentData.expiryTime}
                onChange={handleInputChange}
                required
              />
            </div>
            <small className="expiry-hint">
              메시지가 자동으로 만료될 일시를 설정하세요
            </small>
          </div>

          {/* 텍스트 편집 영역 - 통합된 스타일 텍스트 에어리어 */}
          <div className="text-editor-section">
            <h3>메시지 내용 * (128x192 PNG로 생성됩니다)</h3>

            {/* 텍스트 스타일 컨트롤 */}
            <div className="text-style-controls">
              <div className="control-group">
                <label>정렬:</label>
                <select
                  value={currentData.textAlign}
                  onChange={(e) => handleStyleChange('textAlign', e.target.value)}
                >
                  <option value="left">왼쪽 정렬</option>
                  <option value="center">가운데 정렬</option>
                  <option value="right">오른쪽 정렬</option>
                </select>
              </div>

              <div className="control-group">
                <label>크기:</label>
                <select
                  value={currentData.fontSize}
                  onChange={(e) => handleStyleChange('fontSize', e.target.value)}
                >
                  <option value="8">8px</option>
                  <option value="10">10px</option>
                  <option value="12">12px</option>
                  <option value="14">14px</option>
                  <option value="16">16px</option>
                  <option value="18">18px</option>
                  <option value="20">20px</option>
                  <option value="22">22px</option>
                  <option value="24">24px</option>
                  <option value="26">26px</option>
                  <option value="28">28px</option>
                  <option value="30">30px</option>
                  <option value="32">32px</option>
                  <option value="34">34px</option>
                  <option value="36">36px</option>
                  <option value="38">38px</option>
                  <option value="40">40px</option>
                  <option value="42">42px</option>
                  <option value="44">44px</option>
                  <option value="46">46px</option>
                  <option value="48">48px</option>
                </select>
              </div>

              <div className="control-group">
                <label>색상:</label>
                <input
                  type="color"
                  value={currentData.textColor}
                  onChange={(e) => handleStyleChange('textColor', e.target.value)}
                  title="텍스트 색상"
                />
              </div>

              <div className="control-group formatting-buttons">
                <label>스타일:</label>
                <button
                  type="button"
                  className={`format-btn ${currentData.fontWeight === 'bold' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('fontWeight', currentData.fontWeight === 'bold' ? 'normal' : 'bold')}
                  title="굵게"
                >
                  <strong>B</strong>
                </button>
                <button
                  type="button"
                  className={`format-btn ${currentData.fontStyle === 'italic' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('fontStyle', currentData.fontStyle === 'italic' ? 'normal' : 'italic')}
                  title="기울임"
                >
                  <em>I</em>
                </button>
                <button
                  type="button"
                  className={`format-btn ${currentData.textDecoration === 'underline' ? 'active' : ''}`}
                  onClick={() => handleStyleChange('textDecoration', currentData.textDecoration === 'underline' ? 'none' : 'underline')}
                  title="밑줄"
                >
                  <u>U</u>
                </button>
              </div>
            </div>

            {/* 🔥 통합된 스타일 텍스트 에어리어 */}
            <div className="styled-textarea-container">
              <textarea
                ref={textareaRef}
                value={currentData.messageContent}
                onChange={handleTextChange}
                placeholder="메시지 내용을 입력하세요..."
                className="styled-textarea"
                maxLength={200} // 텍스트 길이 제한
                style={{
                  width: '128px',
                  height: '192px',
                  backgroundColor: '#000000',
                  color: currentData.textColor,
                  fontSize: `${currentData.fontSize}px`,
                  fontWeight: currentData.fontWeight,
                  fontStyle: currentData.fontStyle,
                  textDecoration: currentData.textDecoration,
                  textAlign: currentData.textAlign,
                  border: '1px solid #ccc',
                  padding: '8px',
                  fontFamily: '"Nanum Gothic", "나눔고딕", Arial, sans-serif',
                  resize: 'none',
                  outline: 'none',
                  whiteSpace: 'pre-wrap',
                  overflow: 'hidden',
                  boxSizing: 'border-box',
                  // 포커스 상태 개선
                  transition: 'box-shadow 0.2s ease'
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = '0 0 0 2px #007bff';
                }}
                onBlur={(e) => {
                  e.target.style.boxShadow = 'none';
                }}
              />
              <div className="textarea-info">
                <small className="textarea-hint">
                  위 텍스트 에어리어가 그대로 PNG 이미지로 변환됩니다
                </small>
                <small className="character-count">
                  {currentData.messageContent.length}/200자
                </small>
              </div>
            </div>
          </div>

          {/* 등록 버튼 */}
          <div className="form-actions">
            <button
              type="submit"
              className="register-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'PNG 파일 생성 중...' : 'PNG 파일로 등록'}
            </button>
          </div>

          {/* 메시지 표시 */}
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('성공') ? 'success' : 'error'}`}>
              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
                {submitMessage}
              </pre>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default MissingPersonRegistrationPage;